/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Skeleton } from '#UNSAFE_Skeleton';
import { classNames } from '#utils/UNSAFE_classNames/classNames';
import { ComponentChildren } from 'preact';
import { dataGridCellStyles } from './themes/DataGridCell.css';

export type DataGridCellProps<D> = {
  cell: {
    cellType: string;
    rowStart: number;
    rowExtent: number;
    columnStart: number;
    columnExtent: number;
    data?: D;
    lastColIndex?: boolean;
    lastRowIndex?: boolean;
    borderLeft?: boolean;
    borderRight?: boolean;
    level?: number;
  };
  height: number;
  width: number;
};

const getCellStyle = (
  cell: {
    cellType: string;
    level?: number;
    rowStart: number;
    rowExtent: number;
    columnStart: number;
    columnExtent: number;
  },
  width: number,
  height: number
) => {
  let styleString = `
    grid-row-start: ${cell.rowStart + 1};
    grid-row-end: ${cell.rowStart + cell.rowExtent + 1};
    grid-column-start: ${cell.columnStart + 1};
    grid-column-end: ${cell.columnStart + cell.columnExtent + 1};
  `;

  switch (cell.cellType) {
    case 'rowHeader':
    case 'rowHeaderSkeleton':
      styleString += `left: ${width * (cell.level as number)}px;`;
      break;
    case 'rowEndHeader':
    case 'rowEndHeaderSkeleton':
      styleString += `right: ${width * (cell.level as number)}px;`;
      break;
    case 'columnEndHeader':
    case 'columnEndHeaderSkeleton':
      styleString += `bottom: ${height * (cell.level as number)}px;`;
      break;
    case 'columnHeader':
    case 'columnHeaderSkeleton':
      styleString += `top: ${height * (cell.level as number)}px;`;
      break;
    case 'rowHeaderLabel':
    case 'columnHeaderLabel':
      styleString += `
        top: ${height * cell.rowStart}px;
        left: ${width * cell.columnStart}px;
      `;
      break;
    case 'rowEndHeaderLabel':
      styleString += `right: ${width * (cell.level as number)}px;`;
      break;
    case 'columnEndHeaderLabel':
      styleString += `bottom: ${height * (cell.level as number)}px;`;
      break;
  }

  return styleString.trim();
};

const getCellClasses = (cell: {
  cellType: string;
  lastRow?: boolean;
  lastColumn?: boolean;
  borderLeft?: boolean;
  borderTop?: boolean;
}) => {
  const baseClass = dataGridCellStyles.base;
  const headerTypes = [
    'rowHeader',
    'rowEndHeader',
    'columnEndHeader',
    'columnHeader',
    'rowHeaderSkeleton',
    'rowEndHeaderSkeleton',
    'columnEndHeaderSkeleton',
    'columnHeaderSkeleton'
  ];
  const topLeftTypes = ['rowHeaderLabel', 'columnHeaderLabel', 'topLeftSpacer'];
  const topRightTypes = ['rowEndHeaderLabel', 'topRightSpacer'];
  const bottomLeftTypes = ['columnEndHeaderLabel', 'bottomLeftSpacer'];
  const bottomRightTypes = ['bottomRightSpacer'];

  const classes = [baseClass];

  if (headerTypes.includes(cell.cellType)) {
    classes.push(dataGridCellStyles.header);
  } else if (topLeftTypes.includes(cell.cellType)) {
    classes.push(dataGridCellStyles.topLeft);
  } else if (topRightTypes.includes(cell.cellType)) {
    classes.push(dataGridCellStyles.topRight);
  } else if (bottomLeftTypes.includes(cell.cellType)) {
    classes.push(dataGridCellStyles.bottomLeft);
  } else if (bottomRightTypes.includes(cell.cellType)) {
    classes.push(dataGridCellStyles.bottomRight);
  } else if (cell.cellType === 'rowHeaderSpacer') {
    classes.push(dataGridCellStyles.rhSpacer);
  } else if (cell.cellType === 'columnHeaderSpacer') {
    classes.push(dataGridCellStyles.chSpacer);
  }

  if (cell.lastRow) {
    classes.push(dataGridCellStyles.lastRow);
  }
  if (cell.lastColumn) {
    classes.push(dataGridCellStyles.lastColumn);
  }
  if (cell.borderLeft) {
    classes.push(dataGridCellStyles.borderLeft);
  }
  if (cell.borderTop) {
    classes.push(dataGridCellStyles.borderTop);
  }

  return classNames(classes);
};

export function DataGridCell<D>({ cell, height, width }: DataGridCellProps<D>) {
  if (cell.cellType.includes('Skeleton')) {
    return (
      <div style={getCellStyle(cell, width, height)} class={getCellClasses(cell)}>
        <Skeleton height="6x" width="16x" />
      </div>
    );
  }
  return (
    <div style={getCellStyle(cell, width, height)} class={getCellClasses(cell)}>
      {cell.data as ComponentChildren}
    </div>
  );
}
